import { emptyForecastResult } from "../models/forecastResult";
import { normalizeItemKey } from "../models/salesRecord";
import { filterRecordsByBranch } from "./firestoreService";

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function aggregateMonthly(records) {
  /** @type {Record<string, number>} */
  const monthly = {};
  for (const r of records) {
    monthly[r.monthKey] = (monthly[r.monthKey] || 0) + r.qty;
  }
  return monthly;
}

function filterRecordsByMonthWindow(records, monthWindow) {
  if (!monthWindow || monthWindow === "all") return records;
  const n = Number(monthWindow);
  if (!Number.isFinite(n) || n <= 0) return records;

  const monthKeys = [...new Set(records.map((r) => r.monthKey))].sort();
  const allowed = new Set(monthKeys.slice(-n));
  return records.filter((r) => allowed.has(r.monthKey));
}

function computeTrend(monthly, sortedKeys) {
  if (sortedKeys.length < 4) return "stable";
  const last3 = sortedKeys.slice(-3);
  const prev3 = sortedKeys.slice(-6, -3);
  if (prev3.length < 3) return "stable";

  const avgLast =
    last3.reduce((s, k) => s + (monthly[k] || 0), 0) / last3.length;
  const avgPrev =
    prev3.reduce((s, k) => s + (monthly[k] || 0), 0) / prev3.length;

  if (avgPrev <= 0) return avgLast > 0 ? "increasing" : "stable";
  const change = (avgLast - avgPrev) / avgPrev;
  if (change > 0.05) return "increasing";
  if (change < -0.05) return "decreasing";
  return "stable";
}

/**
 * @param {Array<{ itemName: string, currentStock: number|null, branchName: string }>} excelItems
 * @param {Map<string, import("../models/salesRecord").SalesRecord[]>} salesByItem
 * @param {{ branchFilter?: string, monthWindow?: string }} options
 */
export function computeForecasts(excelItems, salesByItem, options = {}) {
  const { branchFilter = "all", monthWindow = "all" } = options;
  const now = new Date();
  const forecastMonth = now.getMonth() + 1;
  const forecastYear = now.getFullYear();
  const prevYearKey = `${forecastYear - 1}-${String(forecastMonth).padStart(2, "0")}`;

  return excelItems.map((item) => {
    const key = normalizeItemKey(item.itemName);
    let records = salesByItem.get(key) || [];

    const itemBranch = item.branchName || branchFilter;
    records = filterRecordsByBranch(records, itemBranch !== "" ? itemBranch : branchFilter);
    records = filterRecordsByMonthWindow(records, monthWindow);

    if (!records.length) {
      return emptyForecastResult(item.itemName, item.branchName, item.currentStock);
    }

    const monthly = aggregateMonthly(records);
    const sortedKeys = Object.keys(monthly).sort();
    const totalSales = sortedKeys.reduce((s, k) => s + monthly[k], 0);
    const monthsOfHistory = sortedKeys.length;
    const avgMonthlySales = monthsOfHistory ? totalSales / monthsOfHistory : 0;

    const dates = records.map((r) => r.tranDate).sort();
    const first = dates[0];
    const last = dates[dates.length - 1];
    const daySpan =
      first && last
        ? Math.max(
            1,
            Math.round(
              (new Date(`${last}T12:00:00`).getTime() -
                new Date(`${first}T12:00:00`).getTime()) /
                86400000
            ) + 1
          )
        : 30;
    const avgDailySales = totalSales / daySpan;

    const last3Keys = sortedKeys.slice(-3);
    const last3MonthsAvg =
      last3Keys.length > 0
        ? last3Keys.reduce((s, k) => s + monthly[k], 0) / last3Keys.length
        : 0;

    let forecast15Days;
    if (last3Keys.length >= 3) {
      const [k3, k2, k1] = last3Keys;
      const [y1, m1] = k1.split("-").map(Number);
      const dim = daysInMonth(y1, m1 - 1);
      const weighted =
        (monthly[k1] || 0) * 0.5 +
        (monthly[k2] || 0) * 0.3 +
        (monthly[k3] || 0) * 0.2;
      forecast15Days = (weighted / dim) * 15;
    } else {
      forecast15Days = avgDailySales * 15;
    }

    const sameMonthPrevYearSales = monthly[prevYearKey] ?? null;
    if (
      sameMonthPrevYearSales != null &&
      avgMonthlySales > 0 &&
      sameMonthPrevYearSales > avgMonthlySales
    ) {
      const pct = (sameMonthPrevYearSales - avgMonthlySales) / avgMonthlySales;
      forecast15Days *= 1 + pct;
    }

    const safetyStock = avgDailySales * 5;
    const recommendedQty = Math.ceil(forecast15Days + safetyStock);

    let purchaseQty = null;
    if (item.currentStock != null) {
      purchaseQty = Math.max(recommendedQty - item.currentStock, 0);
    }

    let growthPercent = null;
    if (sameMonthPrevYearSales != null && sameMonthPrevYearSales > 0) {
      const latestKey = sortedKeys[sortedKeys.length - 1];
      const latestMonthSales = monthly[latestKey] || 0;
      growthPercent = ((latestMonthSales - sameMonthPrevYearSales) / sameMonthPrevYearSales) * 100;
    }

    return {
      itemName: item.itemName,
      branchName: item.branchName || branchFilter || "All",
      currentStock: item.currentStock,
      totalSales,
      avgMonthlySales,
      avgDailySales,
      last3MonthsAvg,
      sameMonthPrevYearSales,
      growthPercent,
      forecast15Days,
      safetyStock,
      recommendedQty,
      purchaseQty,
      trend: computeTrend(monthly, sortedKeys),
      monthlySales: monthly,
      monthsOfHistory,
    };
  });
}

/** Last 12 month keys ending at current month for charts. */
export function last12MonthSeries(monthlySales) {
  const now = new Date();
  const out = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "short", year: "2-digit" });
    out.push({ monthKey: key, label, qty: monthlySales[key] || 0 });
  }
  return out;
}
